/**
 * civilRespondActions.tsx — Action history for the logged-in civil respondent.
 *
 * Lists the user's own checks, with the match score and target type.
 * Pulls from /api/civil-respondent/actions.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, AlertCircle, X } from 'lucide-react';

export default function CivilRespondActions() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listCivilActions()
      .then(setActions)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="px-4 py-3 bg-card border-b border-border flex items-center gap-3">
        <button
          onClick={() => navigate('/civil-respondent')}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-semibold text-foreground">{t('civil.common.actionHistory')}</h1>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        {loading ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('common.loading')}</div>
        ) : error ? (
          <div className="text-destructive text-sm py-8 text-center">{error}</div>
        ) : actions.length === 0 ? (
          <div className="text-muted-foreground text-sm py-8 text-center">{t('civil.common.noActions')}</div>
        ) : (
          <div className="space-y-3">
            {actions.map((a) => {
              const actionMeta = getActionMeta(a.action_type, t);
              return (
                <div
                  key={a.id}
                  className="bg-card border border-border rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {a.action_type === 'false_positive' ? (
                        <X className="w-4 h-4 text-foreground" />
                      ) : a.match_score && a.match_score > 0 ? (
                        <CheckCircle className="w-4 h-4 text-primary" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="font-medium text-foreground">
                        {getTypeLabel(a.target_type, t)}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${actionMeta.color}`}>
                      {actionMeta.label}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>{new Date(a.created_at).toLocaleString()}</div>
                    {a.match_score !== null && a.match_score !== undefined && (
                      <div>{t('civil.common.matchScore')}: {(a.match_score * 100).toFixed(1)}%</div>
                    )}
                    {a.notes && <div className="italic mt-1">{a.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// Helpers at module level so Vite/Rollup's minifier doesn't lose the
// `t` binding when destructuring from useTranslation() inside a const
// initializer inside the component body.
function getTypeLabel(targetType: string, t: (key: string) => string): string {
  if (targetType === 'missing_person') return t('civil.missing.title');
  if (targetType === 'most_wanted') return t('civil.mostWanted.title');
  if (targetType === 'stolen_vehicle') return t('civil.stolenVehicles.title');
  return targetType;
}

function getActionMeta(actionType: string, t: (key: string) => string): { label: string; color: string } {
  if (actionType === 'family_contacted') {
    return { label: t('civil.common.actions.familyContacted'), color: 'bg-primary/10 text-primary' };
  }
  if (actionType === 'action_completed') {
    return { label: t('civil.common.actions.actionCompleted'), color: 'bg-blue-100 text-blue-700' };
  }
  if (actionType === 'false_positive') {
    return { label: t('civil.common.actions.falsePositive'), color: 'bg-amber-100 text-amber-700' };
  }
  return { label: t('civil.common.actions.check'), color: 'bg-accent text-foreground' };
}
